import { ClockIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { Input } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { ChatEnum } from '../../../../Chat.enum.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import LegacyInput from '../LegacyInput/LegacyInput.component.tsx';
import SchedulingRiskWarning from '../../../SchedulingRiskWarning/SchedulingRiskWarning.component.tsx';
import useProgrammedMessageAction from './ProgrammedMessageAction.hook.ts';
import './ProgrammedMessageAction.styles.scss';

export default function ProgramMessageActionContent() {
  const { selectedFollowUp, setSelectedFollowUp, setTemplatePickerRequest } =
    useChatContext();
  const { setSelectedAction, setMessageBarValue, setIsSchedulePickerMode } =
    useMessageBarContext();
  const { t } = useTranslation('chat/chat');
  const {
    loading,
    triggerDate,
    triggerTime,
    localDate,
    localTime,
    followUpAttachments,
    setTriggerDate,
    setTriggerTime,
    setFollowUpAttachments,
    handleTriggerDateTimeChange,
    isDateInPast,
    isEmpty,
    wapiScheduleLimit,
    maxDate,
    maxTime,
  } = useProgrammedMessageAction();

  function handleCancel() {
    setSelectedFollowUp(null);
    setFollowUpAttachments(undefined);
    setTriggerDate('');
    setTriggerTime('');
    setMessageBarValue('');
    setSelectedAction(ChatEnum.CHAT);
  }

  // INT-1500 U13 (D16): "Usar una plantilla" leaves this panel for the
  // template picker in scheduling mode, through the composer's own gate (no
  // approved template sends the seller to create one). What was typed stays in
  // the composer.
  function handleUseTemplate() {
    setSelectedFollowUp(null);
    setFollowUpAttachments(undefined);
    setTriggerDate('');
    setTriggerTime('');
    setIsSchedulePickerMode(true);
    setTemplatePickerRequest({ templateType: 'ALL' });
  }

  const isSaveDisabled =
    loading || !triggerDate || !triggerTime || isDateInPast || isEmpty;
  // Switching to a template would leave an edited card behind unchanged, so
  // the switch is offered only while creating one.
  const isCreatingNew = !selectedFollowUp?.id;

  return (
    <div className="ProgramMessageAction">
      <div className="ProgramMessageAction__Header">
        <div className="ProgramMessageAction__Left">
          <strong>{t('programmedMessageAction.scheduleMessage')}</strong>
          <div className="ProgramMessageAction__Left__InputContainer">
            <CalendarIcon className="ProgramMessageAction__Left__InputContainer__Icon" />
            <Input
              size="sm"
              value={triggerDate ?? ''}
              min={localDate}
              max={maxDate ?? undefined}
              onChange={(e) => setTriggerDate(e.target.value)}
              className="ProgramMessageAction__Left__Input"
              type="date"
            />
          </div>

          <strong>{t('programmedMessageAction.scheduleAt')}</strong>
          <div className="ProgramMessageAction__Left__InputContainer">
            <ClockIcon className="ProgramMessageAction__Left__InputContainer__Icon" />
            <Input
              size="sm"
              value={triggerTime ?? ''}
              min={localTime}
              max={maxDate && triggerDate === maxDate ? maxTime ?? undefined : undefined}
              onChange={(e) => setTriggerTime(e.target.value)}
              className="ProgramMessageAction__Left__Input"
              type="time"
            />
          </div>

        </div>
        <XMarkIcon
          onClick={handleCancel}
          className="ProgramMessageAction__Xicon"
        />
      </div>

      {wapiScheduleLimit && maxDate && maxTime && (
        <div className="ProgramMessageAction__LimitBanner">
          <div className="ProgramMessageAction__LimitBanner__Left">
            <InformationCircleIcon className="ProgramMessageAction__LimitBanner__Icon" />
            <span>{t('programmedMessageAction.limitLabel')} <strong>{wapiScheduleLimit}</strong></span>
          </div>
          <button
            type="button"
            className="ProgramMessageAction__LimitBanner__Button"
            onClick={() => {
              setTriggerDate(maxDate);
              setTriggerTime(maxTime);
            }}
          >
            {t('programmedMessageAction.useThisTime')}
          </button>
        </div>
      )}

      {isDateInPast && (
        <p className="text-xs text-error bg-error/20 border-l-2 border-error px-4 py-1.5">
          {t('programmedMessageAction.pastDateError')}
        </p>
      )}

      {isEmpty && !isDateInPast && (
        <p className="text-xs text-base-content/60 bg-base-200 border-l-2 border-base-300 px-4 py-1.5">
          {t('programmedMessageAction.emptyContentError')}
        </p>
      )}

      <SchedulingRiskWarning
        triggerDate={triggerDate}
        triggerTime={triggerTime}
        content={{ kind: 'text' }}
        onScheduleAnyway={handleTriggerDateTimeChange}
        isScheduleDisabled={isSaveDisabled}
        onUseTemplate={isCreatingNew ? handleUseTemplate : undefined}
      />

      <LegacyInput
        attachments={followUpAttachments}
        setNewAttachments={setFollowUpAttachments}
        actionButtonText={t('programmedMessageAction.saveButton')}
        actionButtonLoading={loading}
        actionButtonDisabled={isSaveDisabled}
        handleActionButton={handleTriggerDateTimeChange}
        cancelButtonText={t('programmedMessageAction.cancel')}
        onCancel={handleCancel}
      />
    </div>
  );
}
